import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { emailNotificationApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import Card from '@/components/ui/Card'
import Alert from '@/components/ui/Alert'
import Loading from '@/components/ui/Loading'
import { Bell, Mail, Shield } from 'lucide-react'

interface NotificationSetting {
  id: number
  action_name: string
  display_name: string
  description: string
  is_enabled: boolean
}

export default function EmailNotifications() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['email-notifications'],
    queryFn: () => emailNotificationApi.list(),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_enabled }: { id: number; is_enabled: boolean }) =>
      emailNotificationApi.toggle(id, is_enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-notifications'] })
      setSuccess('Notification setting updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update notification setting')
      setTimeout(() => setError(''), 5000)
    },
  })

  const handleToggle = (id: number, currentValue: boolean) => {
    toggleMutation.mutate({ id, is_enabled: !currentValue })
  }

  // Check if user is super admin
  if (user?.user_type !== 'super_admin') {
    return (
      <div className="max-w-6xl mx-auto">
        <Alert
          type="error"
          message="Access Denied: Only Super Admins can manage email notification settings."
        />
      </div>
    )
  }

  if (isLoading) return <Loading />

  const notifications: NotificationSetting[] = (data?.data as any)?.settings || []

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Bell className="w-8 h-8 text-primary-600" />
        <div>
          <h1 className="text-3xl font-bold">Email Notification Settings</h1>
          <p className="text-gray-600 mt-1">
            Configure which actions trigger email notifications to Super Admin
          </p>
        </div>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">About Email Notifications:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Notifications are sent to the Super Admin email address</li>
              <li>Toggle each notification type on or off based on your preferences</li>
              <li>Changes take effect immediately</li>
              <li>These settings help you stay informed about important platform activities</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Notification Settings Grid */}
      <Card title="Notification Toggles">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="flex items-start justify-between p-5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
            >
              <div className="flex items-start space-x-3 flex-1">
                <Mail className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900 mb-1">{notification.display_name}</p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {notification.description}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleToggle(notification.id, notification.is_enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ml-4 flex-shrink-0 ${
                  notification.is_enabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
                disabled={toggleMutation.isPending}
                aria-label={`Toggle ${notification.display_name}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notification.is_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>

        {notifications.length === 0 && (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No notification settings available</p>
          </div>
        )}
      </Card>

      {/* Notification Categories Info */}
      <Card title="Notification Categories" className="bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">User Actions</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• User registrations</li>
              <li>• Profile updates</li>
              <li>• Subscription changes</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Barter Activity</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Barter creations</li>
              <li>• Barter updates</li>
              <li>• Group formations</li>
              <li>• Status changes</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Financial Events</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Coin purchases</li>
              <li>• Withdrawals</li>
              <li>• Escrow transactions</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  )
}
